import ConfirmModal from '@/components/confirm-modal'
import {
    WaitingListEntry,
    deleteWaitingListEntry,
    notifyWaitingListEntry,
} from '@/lib/api/waiting-list/waiting-list-api'
import { useState } from 'react'
import {
    MdAdd,
    MdExpandLess,
    MdExpandMore,
    MdHourglassEmpty,
} from 'react-icons/md'
import { toast } from 'react-toastify'
import { WaitingListCard } from './WaitingListCard'
import { WaitingListModal } from './WaitingListModal'

interface WaitingListPanelProps {
    agendaId: number
    entries: WaitingListEntry[]
    onRefresh: () => void
    onSchedule?: (entry: WaitingListEntry) => void
    collapsible?: boolean
    defaultCollapsed?: boolean
}

export function WaitingListPanel({
    agendaId,
    entries,
    onRefresh,
    onSchedule,
    collapsible = true,
    defaultCollapsed = false,
}: WaitingListPanelProps) {
    const [collapsed, setCollapsed] = useState(defaultCollapsed)
    const [showModal, setShowModal] = useState(false)
    const [selectedEntry, setSelectedEntry] = useState<WaitingListEntry | null>(
        null
    )
    const [showDeleteModal, setShowDeleteModal] = useState(false)
    const [entryToDelete, setEntryToDelete] = useState<WaitingListEntry | null>(
        null
    )
    const [deleting, setDeleting] = useState(false)
    const [notifying, setNotifying] = useState<number | null>(null)

    const waitingEntries = entries.filter(e => e.status === 'WAITING')

    const handleAdd = () => {
        setSelectedEntry(null)
        setShowModal(true)
    }

    const handleEdit = (entry: WaitingListEntry) => {
        setSelectedEntry(entry)
        setShowModal(true)
    }

    const handleDelete = (entry: WaitingListEntry) => {
        setEntryToDelete(entry)
        setShowDeleteModal(true)
    }

    const handleNotify = async (entry: WaitingListEntry) => {
        setNotifying(entry.id)
        try {
            const response = await notifyWaitingListEntry(entry.id)
            if (response.success) {
                toast.success('Notificação enviada com sucesso')
                onRefresh()
            } else {
                toast.error(response.error || 'Erro ao enviar notificação')
            }
        } catch (error) {
            toast.error('Erro ao enviar notificação')
        } finally {
            setNotifying(null)
        }
    }

    const confirmDelete = async () => {
        if (!entryToDelete) return

        setDeleting(true)
        try {
            const response = await deleteWaitingListEntry(entryToDelete.id)
            if (response.success) {
                toast.success('Removido da lista de espera')
                setShowDeleteModal(false)
                setEntryToDelete(null)
                onRefresh()
            } else {
                toast.error(response.error || 'Erro ao remover')
            }
        } catch (error) {
            toast.error('Erro ao remover')
        } finally {
            setDeleting(false)
        }
    }

    const handleCloseModal = () => {
        setShowModal(false)
        setSelectedEntry(null)
    }

    return (
        <div className="bg-base-100 rounded-xl border border-base-300/50">
            <div
                className={`flex items-center justify-between p-4 ${collapsible ? 'cursor-pointer' : ''
                    }`}
                onClick={() => collapsible && setCollapsed(!collapsed)}
            >
                <div className="flex items-center gap-2">
                    <MdHourglassEmpty className="text-xl text-amber-500" />
                    <h3 className="font-semibold text-base-content">
                        Lista de Espera
                    </h3>
                    {waitingEntries.length > 0 && (
                        <span className="badge badge-sm badge-warning">
                            {waitingEntries.length}
                        </span>
                    )}
                </div>
                <div className="flex items-center gap-2">
                    <button
                        className="btn btn-sm btn-ghost btn-square"
                        onClick={e => {
                            e.stopPropagation()
                            handleAdd()
                        }}
                        title="Adicionar à lista de espera"
                    >
                        <MdAdd className="text-lg" />
                    </button>
                    {collapsible && (
                        <button className="btn btn-sm btn-ghost btn-square">
                            {collapsed ? (
                                <MdExpandMore className="text-lg" />
                            ) : (
                                <MdExpandLess className="text-lg" />
                            )}
                        </button>
                    )}
                </div>
            </div>

            {!collapsed && (
                <div className="px-4 pb-4 space-y-3 max-h-[400px] overflow-y-auto">
                    {entries.length === 0 ? (
                        <div className="text-center py-6">
                            <MdHourglassEmpty className="text-4xl text-base-content/30 mx-auto mb-2" />
                            <p className="text-sm text-base-content/60">
                                Nenhuma pessoa na lista de espera
                            </p>
                            <button
                                className="btn btn-sm btn-secondary mt-3"
                                onClick={handleAdd}
                            >
                                <MdAdd className="text-lg" />
                                Adicionar
                            </button>
                        </div>
                    ) : (
                        entries.map(entry => (
                            <WaitingListCard
                                key={entry.id}
                                entry={entry}
                                onSchedule={onSchedule}
                                onNotify={handleNotify}
                                onEdit={handleEdit}
                                onDelete={handleDelete}
                            />
                        ))
                    )}
                </div>
            )}

            {showModal && (
                <WaitingListModal
                    agendaId={agendaId}
                    entry={selectedEntry}
                    onClose={handleCloseModal}
                    onSuccess={onRefresh}
                />
            )}

            <ConfirmModal
                isOpen={showDeleteModal}
                onClose={() => {
                    setShowDeleteModal(false)
                    setEntryToDelete(null)
                }}
                onConfirm={confirmDelete}
                title="Remover da lista de espera"
                message={`Tem certeza de que deseja remover ${entryToDelete?.client?.name} da lista de espera?`}
                confirmButtonText="Remover"
                cancelButtonText="Cancelar"
                loading={deleting}
            />
        </div>
    )
}
